import { finalize, tap } from 'rxjs/operators';
import Swal from 'sweetalert2';
import { TokenInfo, User } from '../../shared/models';
import { Injectable } from '@angular/core';
import { EventsHubService } from '../events-hub.service';
import { LoggerService, UserService } from '../data-services';
import { BannerNotificationsService, LocalStorageService } from '../../xform-compat';
import { Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { Observable } from 'rxjs/internal/Observable';
import { GoogleTagManagerService } from './analytics/google-tag-manager.service';
import { environment } from '../../../environments/environment';


@Injectable({
  providedIn: 'root'
})
export class AuthenticationService {

  private loggedUser: User;
  private tokenInfo: TokenInfo;
  private readonly COOKIE_BRIDGE_KEY = 'rms-bridge-tokenInfo';
  private readonly COOKIE_BRIDGE_EXPIRY_DAYS = 7;
  private readonly SSO_FLOW_MARKER_KEY = 'rms-oidc-flow-pending';
  private loggingOut = false;
  /** True when this session's token came from the iOS cookie bridge rather than localStorage. */
  private restoredFromBridge = false;

  constructor(
    private eventsHubService: EventsHubService,
    private loggerService: LoggerService,
    private storage: LocalStorageService,
    private router: Router,
    private translateService: TranslateService,
    private eventsHub: EventsHubService,
    private userService: UserService,
    private bannerNotificationService: BannerNotificationsService,
    private googleTagManagerService: GoogleTagManagerService
  ) {
    this.restoreFromCookieBridge();
    this.tokenInfo = this.getTokenInfo();

    const lang = this.getLang();
    if (lang) {
 this.translateService.use(lang);
}

    /*
     * Seed analytics identity for sessions that are already logged in (deep links, refreshes).
     * This runs before any component can push a view event, which is why identity lives here
     * rather than in HeaderComponent (finding H3). See analytics/CLAUDE.md.
     */
    const existingUser = this.getLoggedUser();
    // `!= null`, not truthiness: user id 0 is the valid system account and `if (0)` would skip it.
    if (existingUser?.id != null) {
      this.googleTagManagerService.setUser(existingUser.id);
    }

    this.eventsHubService.profileUpdated$.subscribe(
      () => this.refreshLoggedUserInfo()
    );
  }

  /**
   * Deferred startup work that must not run inside the constructor. Invoked from an
   * APP_INITIALIZER in AppModule.
   *
   * Repairs a session that has a token but no stored user. The iOS cookie bridge produces this
   * state by design — it restores `tokenInfo` only, while `clearLocalStorage()` removed
   * `loggedUser` alongside it — but any path that drops `loggedUser` while leaving `tokenInfo`
   * behind lands in the same place: `isLoggedIn()` is true, `getLoggedUser()` is null, the header
   * renders without a user, and analytics can never attribute the session. Re-fetching the user
   * repairs all of it and, via setLoggedUser(), sets analytics identity as a side effect.
   * This is finding C1; note its measured analytics cost was ~0, so the justification is session
   * integrity (header and permissions also break in this state), not attribution.
   *
   * HTTP cannot be issued from the constructor: AuthInterceptor injects this service, so a
   * request during construction would create a circular dependency.
   */
  public init(): void {
    if (this.isLoggedIn() && !this.getLoggedUser()) {
      if (!environment.production) {
        const source = this.restoredFromBridge ? 'restored from the iOS cookie bridge' : 'present in localStorage';
        console.warn(`[auth] Token ${source} but no loggedUser is stored; re-fetching the user.`);
      }
      this.refreshLoggedUserInfo();
    }
  }

  public setTokenInfoFromOkta(tokenInfo: TokenInfo): void {
    this.tokenInfo = tokenInfo;
    this.storage.set('tokenInfo', this.tokenInfo);
    this.writeCookieBridge(tokenInfo);
  }

  /**
   * Marks that this browser tab is about to redirect to the IdP to start an SSO login.
   * sessionStorage is scoped to this tab, so the marker cannot be set by a flow an attacker
   * ran in their own browser. okta-login checks for it via consumeSsoFlowMarker() to reject
   * an exchangeKey delivered by a crafted link to a browser that never started this flow.
   */
  public beginSsoFlow(): void {
    sessionStorage.setItem(this.SSO_FLOW_MARKER_KEY, '1');
  }

  /**
   * Single-use check for the marker set by beginSsoFlow(). Returns true only if this tab
   * initiated the SSO flow currently being completed; always clears the marker so it cannot
   * be replayed for a later, unrelated exchangeKey.
   */
  public consumeSsoFlowMarker(): boolean {
    const wasSet = sessionStorage.getItem(this.SSO_FLOW_MARKER_KEY) !== null;
    sessionStorage.removeItem(this.SSO_FLOW_MARKER_KEY);
    return wasSet;
  }

  public userEmail(email: string): Promise<any> {
    return new Promise((resolve, reject) => {
      this.loggerService.getLoginRedirectUrl(email)
        .subscribe(
          (result) => {
            resolve(result);
          },
          (error) => {
            reject(error);
            this.eventsHub.setLoggedIn(false);
          });
    });
  }

  public login(credentials: object): Promise<any> {
    return new Promise((resolve, reject) => {
      this.loggerService.login(credentials)
        .subscribe(
          (token) => {
            this.tokenInfo = new TokenInfo(token);
            this.storage.set('tokenInfo', this.tokenInfo);
            this.writeCookieBridge(this.tokenInfo);
            resolve(this.tokenInfo);
          },
          (error) => {
            reject(error);
            this.eventsHub.setLoggedIn(false);
          });
    });

  }

  /**
   * Promise to get info from the logger service
   * Attempts to get refresh token data
   * updates the user session info
   * @param {TokenInfo} tokenInfo: cached user session data
   * @returns {Observable<any>}: resolves or rejects the promise
   */
  public refreshToken (tokenInfo: TokenInfo): Observable<any> {
    return this.loggerService.refreshToken(tokenInfo.refreshToken).pipe(
      tap(response => {
        // Please note that we are using rotating refresh tokens for security purposes!
        // The updated token info will have a different refresh token from what we had previously.
        this.tokenInfo = new TokenInfo(response);
        this.storage.set('tokenInfo', this.tokenInfo);
        this.writeCookieBridge(this.tokenInfo);
      })
    );
  }

  /**
   * Gets the user data
   */
  private refreshLoggedUserInfo(): void {
    this.userService.getLoggedUser().subscribe(
        (user: User) => {
          this.setLoggedUser(user);
          this.loggedUser = this.getLoggedUser();
        },
        (error) => {
          this.bannerNotificationService.error(error);
        });
  }

  public logout(redirectUrl?: string, expired?: boolean) {
    if (this.loggingOut) {
      return;
    }
    Swal.close();
    const tokenInfo = this.getTokenInfo();
    if (tokenInfo) {
      this.loggingOut = true;
      this.loggerService.logout().pipe(
        finalize(() => {
          this.loggingOut = false;
          this.vacate(redirectUrl, expired);
        })
      ).subscribe();
    } else {
      this.vacate(redirectUrl, expired);
    }
  }


  public isLoggedIn() {
    return !!(this.getTokenInfo());
  }

  public getLoggedUser(): User {
    return this.storage.get('loggedUser');
  }

  /**
   * Stores the logged-in user and hands the id to analytics. This is the single hook that covers
   * every login path (password, Okta, and refreshLoggedUserInfo), so no component needs to push
   * identity itself (findings H3, H4). See analytics/CLAUDE.md.
   */
  public setLoggedUser(user: User) {
    this.storage.set('loggedUser', user);
    this.googleTagManagerService.setUser(user?.id);
  }

  /**
   * Check if the permissions stored have changed
   * @param oldLoggedUserInfo Previous stored user data
   * @param newLoggedUserInfo Refreshed user data
   * @returns {boolean} If there are changes on permissions
   */
  public permissionsChanged(oldLoggedUserInfo, newLoggedUserInfo): boolean {
    if (  !oldLoggedUserInfo || !newLoggedUserInfo || oldLoggedUserInfo.superUser !== newLoggedUserInfo.superUser
      || oldLoggedUserInfo.userTenants[0].administrator !== newLoggedUserInfo.userTenants[0].administrator) {
      return true;
    }
    // Check permission ids
    const oldPermissions = new Set();
    const newPermissions = new Set();
    oldLoggedUserInfo.userTenants[0].tenantRoles.forEach(function (role) {
      role.permissions.forEach(permission => oldPermissions.add(permission.id));
    });
    newLoggedUserInfo.userTenants[0].tenantRoles.forEach(function (role) {
      role.permissions.forEach(permission => newPermissions.add(permission.id));
    });
    // newPermissions = [1,3,4]; oldPermissions = [1,4,3] => "1,3,4" !== "1,3,4"
    return (Array.from(newPermissions).sort().toString() !== Array.from(oldPermissions).sort().toString());
  }

  public setLang(userLang: string, defaultLang?: string) {
    this.storage.set('lang', userLang || defaultLang);
  }

  public getLang(): string {
    return this.getLoggedUser() ?
      this.getLoggedUser().attributes.language || this.translateService.getDefaultLang() :
      this.translateService.getDefaultLang();
  }

  public getTokenInfo(): TokenInfo {
    return this.storage.get('tokenInfo') || null;
  }

  public validateToken() {
    const tokenInfo = this.getTokenInfo();
    if (tokenInfo) {
      this.loggerService.validateToken(tokenInfo.accessToken)
        .subscribe(
          () => {},
          () => this.vacate()
        );
    } else {
      this.vacate();
    }
  }

  /**
   * Tears down the session in-tab. Logout is an SPA navigation with no page reload, so analytics
   * identity has to be cleared explicitly or the next user's events are attributed to this one
   * (finding C2). See analytics/CLAUDE.md.
   *
   * The stored user is deleted alongside the in-memory field, not just nulled. Every path here ends
   * in a router.navigate(), and AnalyticsIdentityService re-asserts identity on NavigationStart by
   * reading getLoggedUser() — so a `loggedUser` left in storage is pushed straight back, undoing the
   * clearUser() above.
   *
   * tokenInfo and the cookie bridge are also cleared here, not just in clearLocalStorage(): the
   * validateToken()-failure and no-token paths reach vacate() directly without ever sending the
   * logout request that clearLocalStorage() piggybacks on, so without this an explicit logout on
   * those paths left tokenInfo behind and the session silently came back on the next app start.
   */
  private vacate(redirectUrl?: string, expired?: boolean) {
    this.eventsHub.setLoggedIn(false);
    this.loggedUser = null;
    this.storage.delete('loggedUser');
    this.storage.delete('tokenInfo');
    this.clearCookieBridge();
    this.googleTagManagerService.clearUser();
    if (!redirectUrl) {
      this.router.navigate(['/']);
    } else {
      if (!this.router.url.includes('redirect')) {
        const queryParams = {
          redirectTo: redirectUrl,
          expired: (expired)
        };
        this.router.navigate(['/login'], { queryParams: queryParams });
      }
    }
  }

  public clearLocalStorage(): void {
    /*
     * When the user logs out, clear token info first so that other tabs don't try to log the user out
     * as well
     */
    this.storage.delete('tokenInfo');
    this.storage.delete('loggedUser');
    this.storage.delete('notifications');
    this.clearCookieBridge();
    /*
     * Also cleared here, not just in vacate(): AuthInterceptor calls this method directly when it
     * sees the logout request go out, and the two paths are not always both hit.
     */
    this.googleTagManagerService.clearUser();
  }

  /**
   * On iOS Safari, the Okta redirect breaks out of standalone (home screen) app context
   * and completes in regular Safari. Tokens written to localStorage in Safari are not
   * accessible in the standalone app's isolated storage. Writing them to a cookie bridges
   * this gap, since cookies are shared between Safari and standalone mode on iOS.
   *
   * Also called on login() and refreshToken() so the bridge stays current after token rotation,
   * covering the case where iOS kills the tab mid-session.
   *
   * No Domain attribute is set, so this is a host-only cookie: only this exact origin can read or
   * write it, not same-site subdomains. The 7-day expiry is deliberately longer than the access
   * token's lifetime (expiresIn) — it only needs to survive iOS killing the tab, and
   * AuthInterceptor already refreshes/retries on the first 401 after restore (see
   * restoreFromCookieBridge). Shortening it to the access-token lifetime, or requiring a
   * `__Host-` prefix (which forces Secure and would stop this cookie being set at all on the
   * http:// dev/test origins this app also runs on), would defeat that without closing any real
   * gap, so neither is done here.
   */
  private writeCookieBridge(tokenInfo: TokenInfo): void {
    const expiry = new Date();
    expiry.setDate(expiry.getDate() + this.COOKIE_BRIDGE_EXPIRY_DAYS);
    const secure = window.location.protocol === 'https:' ? '; Secure' : '';
    const value = encodeURIComponent(JSON.stringify(tokenInfo));
    document.cookie = `${this.COOKIE_BRIDGE_KEY}=${value}; expires=${expiry.toUTCString()}; path=/; SameSite=Strict${secure}`;
  }

  /**
   * On startup, if localStorage has no tokenInfo (e.g. standalone app launched fresh),
   * check for a bridge cookie written by a prior login. If found, restore tokenInfo to
   * localStorage so the session continues without re-authentication, then clear the cookie.
   *
   * The restored token may already be expired — the bridge cookie lives 7 days but access tokens
   * do not. Nothing refreshes it up front; the first authenticated request 401s and
   * AuthInterceptor refreshes and retries it.
   *
   * Only tokenInfo travels in the cookie, so `loggedUser` is absent afterwards. `init()` repairs
   * that; see the flag it reads below.
   */
  private restoreFromCookieBridge(): void {
    if (this.storage.get('tokenInfo')) {
      return;
    }
    const raw = this.readCookie(this.COOKIE_BRIDGE_KEY);
    if (!raw) {
      return;
    }
    try {
      const tokenInfo = JSON.parse(decodeURIComponent(raw));
      if (this.isValidTokenInfoShape(tokenInfo)) {
        this.storage.set('tokenInfo', tokenInfo);
        this.restoredFromBridge = true;
      }
    } catch {
      // Ignore a malformed cookie — app will proceed to login normally
    }
    this.clearCookieBridge();
  }

  /**
   * Guards against restoring a cookie that was tampered with or truncated: only the two fields
   * this class actually reads off a restored TokenInfo (accessToken for requests, refreshToken
   * for rotation) are required to be non-empty strings.
   */
  private isValidTokenInfoShape(value: any): boolean {
    return !!value
      && typeof value.accessToken === 'string' && value.accessToken !== ''
      && typeof value.refreshToken === 'string' && value.refreshToken !== '';
  }

  private clearCookieBridge(): void {
    const secure = window.location.protocol === 'https:' ? '; Secure' : '';
    document.cookie = `${this.COOKIE_BRIDGE_KEY}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/; SameSite=Strict${secure}`;
  }

  private readCookie(name: string): string | null {
    const match = document.cookie.match(new RegExp('(^| )' + name + '=([^;]+)'));
    return match ? match[2] : null;
  }

}
